import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Subnav } from '@air/api';
import { SortDirection, type Subnav as SubnavType, type WorkspaceInfo } from '@air/api/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

const SUBNAV_SORT = 'SUBNAV_SORT';

export const getWorkspaceSubnavSortKey = (workspaceId?: WorkspaceInfo['id']) => [SUBNAV_SORT, { workspaceId }];

export const useSubnavSort = (params?: { enabled?: boolean; libraryId?: string }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  // Always use same query key - fetch ALL sorts once
  const key = getWorkspaceSubnavSortKey(currentWorkspace?.id);

  const enabled = params?.enabled ?? true;
  const libraryId = params?.libraryId;

  return useQuery({
    queryKey: key,

    queryFn: async () => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      const response = await Subnav.getSubnav({ workspaceId });

      // TODO: This logic should likely live in the backend, there is a ticket
      // for this but we are writing this here in the meantime to fix bugs[1] and
      // provide the right UX.
      // [1]: https://air-labs-team.slack.com/archives/CKE8SK3M1/p1645652666618349

      return {
        ...response,
        boardSort: {
          name: 'customSort',
          direction: SortDirection.desc,
          ...response?.boardSort,
        },
        libraryBoardSorts: response?.libraryBoardSorts ?? [],
      };
    },
    enabled: enabled && !!currentWorkspace,
    select: (data) => {
      // If libraryId provided, filter to just that library's sort
      if (libraryId) {
        const librarySort = data.libraryBoardSorts?.find((sort) => sort.libraryId === libraryId);
        const defaultLibrarySort = {
          libraryId,
          name: 'customSort',
          direction: SortDirection.desc,
        };

        return {
          ...data,
          libraryBoardSorts: [librarySort ?? defaultLibrarySort],
        };
      }

      return data;
    },
  });
};

export const useGetSubnavSortValue = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const getSubNavSortValue = useCallback(
    (libraryId?: string) => {
      // Always use workspace-level query key (no libraryId)
      const key = getWorkspaceSubnavSortKey(currentWorkspace?.id);
      const data = queryClient.getQueryData<SubnavType>(key);

      // If libraryId provided, find that library's sort from the array
      if (libraryId && data) {
        const librarySort = data.libraryBoardSorts?.find((sort) => sort.libraryId === libraryId);
        const defaultLibrarySort = {
          libraryId,
          name: 'customSort',
          direction: SortDirection.desc,
        };

        return {
          ...data,
          libraryBoardSorts: [librarySort ?? defaultLibrarySort],
        };
      }

      return data;
    },
    [currentWorkspace?.id, queryClient],
  );

  return {
    getSubNavSortValue,
  };
};
